import { Icon } from "@iconify/react/dist/iconify.js";
import { Tag } from "antd";
import { useSelector } from "react-redux";
import Pagination from "../../utils/Pagination";
import { formatDateToDMY } from "../../utils/tools";
import SearchBy from "../../utils/SearchBy";
import { TableSortHeaders } from "../../utils/TableSortHeaders";

const SubscriberTable = ({
    subscribers,
    page,
    setPage,
    totalPages,
    totalEntries,
    sort,
    order,
    setSort,
    setOrder,
    search,
    setSearch,
    entriesPerPage,
    setEntriesPerPage,
    filterPlan,
    setFilterPlan,
    filterPlatform,
    setFilterPlatform,
    onRefresh,
    onView,
    onDelete,
}) => {
    const viewPermissions = useSelector((state) => state.permission.rolePermissions) || [];

    const headerConfig = [
        { key: "id", label: "#", sortable: true },
        { key: "created_at", label: "Subscribed On", sortable: true },
        { key: "name", label: "User Name", sortable: false },
        { key: "email", label: "Email", sortable: false },
        { key: "type", label: "Plan", sortable: true },
        { key: "platform", label: "Platform", sortable: true },
        { key: "is_active", label: "Status", sortable: true },
        { key: "expires_at", label: "Expires On", sortable: true },
        { key: "action", label: "Action", sortable: false },
    ];

    const planColor = (type) => {
        if (type === "yearly") return "gold";
        if (type === "monthly") return "blue";
        return "default";
    };

    const platformIcon = (platform) => {
        if (platform === "android") return <Icon icon="logos:android-icon" width="20" />;
        if (platform === "ios") return <Icon icon="logos:apple" width="18" />;
        return platform;
    };

    const isExpired = (expiresAt) => {
        if (!expiresAt) return true;
        return new Date(expiresAt) < new Date();
    };

    return (
        <div className="card basic-data-table text-sm overflow-x-auto">
            <div className="card-header responsive-header d-flex justify-content-between align-items-center flex-wrap gap-2 global-header">
                <h6 className="mb-0 fw-semibold">Pro Subscribers</h6>
                <div className="d-flex align-items-center flex-wrap gap-2">
                    <select
                        className="form-select form-select-sm"
                        style={{ width: 130 }}
                        value={filterPlan}
                        onChange={(e) => setFilterPlan(e.target.value)}
                        title="Filter by Plan"
                    >
                        <option value="">All Plans</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                    </select>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: 130 }}
                        value={filterPlatform}
                        onChange={(e) => setFilterPlatform(e.target.value)}
                        title="Filter by Platform"
                    >
                        <option value="">All Platforms</option>
                        <option value="android">Android</option>
                        <option value="ios">iOS</option>
                    </select>
                    <button
                        className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                        onClick={onRefresh}
                        title="Refresh"
                    >
                        <Icon icon="mdi:refresh" width={16} />
                        Refresh
                    </button>
                    <SearchBy
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                    />
                </div>
            </div>
            <div className="card-body card-body-modify">
                <div className="w-full overflow-x-auto">
                    <table className="table bordered-table mb-0 text-sm w-full min-w-max" id="dataTable">
                        <thead>
                            <tr>
                                {TableSortHeaders(headerConfig, sort, order, setSort, setOrder, setPage)}
                            </tr>
                        </thead>
                        <tbody>
                            {Array.isArray(subscribers) && subscribers.length > 0 ? (
                                subscribers.map((sub, index) => (
                                    <tr key={sub.id}>
                                        <td>{(page - 1) * entriesPerPage + index + 1}</td>
                                        <td>{sub.created_at ? formatDateToDMY(sub.created_at) : "—"}</td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <img
                                                    src={sub.user?.avatar || "/assets/images/logo-profile.png"}
                                                    alt={sub.user?.name}
                                                    width={32}
                                                    height={32}
                                                    className="rounded-circle"
                                                    style={{ objectFit: "cover" }}
                                                    onError={(e) => { e.target.src = "/assets/images/logo-profile.png"; }}
                                                />
                                                <span>{sub.user?.name || "—"}</span>
                                            </div>
                                        </td>
                                        <td>{sub.user?.email || "—"}</td>
                                        <td>
                                            <Tag color={planColor(sub.type)} className="text-capitalize fw-semibold">
                                                {sub.type || "—"}
                                            </Tag>
                                        </td>
                                        <td>
                                            <span className="d-flex align-items-center gap-1">
                                                {platformIcon(sub.platform)}
                                                <span className="text-capitalize">{sub.platform || "—"}</span>
                                            </span>
                                        </td>
                                        <td>
                                            {sub.is_active && !isExpired(sub.expires_at) ? (
                                                <Tag color="success">Active</Tag>
                                            ) : isExpired(sub.expires_at) ? (
                                                <Tag color="error">Expired</Tag>
                                            ) : (
                                                <Tag color="warning">Inactive</Tag>
                                            )}
                                        </td>
                                        <td>
                                            <span className={isExpired(sub.expires_at) ? "text-danger" : "text-success"}>
                                                {sub.expires_at ? formatDateToDMY(sub.expires_at) : "—"}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <button
                                                    className="w-32-px h-32-px bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center"
                                                    onClick={() => onView(sub)}
                                                    title="View"
                                                >
                                                    <Icon icon="iconamoon:eye-light" />
                                                </button>
                                                <button
                                                    className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                                    onClick={() => onDelete(sub.id)}
                                                    title="Delete"
                                                >
                                                    <Icon icon="mingcute:delete-2-line" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={9} className="text-center py-4">
                                        No subscribers found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <Pagination
                    page={page}
                    setPage={setPage}
                    totalPages={totalPages}
                    totalEntries={totalEntries}
                    entriesPerPage={entriesPerPage}
                    setEntriesPerPage={setEntriesPerPage}
                />
            </div>
        </div>
    );
};

export default SubscriberTable;
